import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import { CiSearch } from "react-icons/ci";
import { LedgerRow, ReportData } from "../types";
import { balanceColor, COLOR, fmt } from "../utils/format";
import { AttachmentButton } from "./AttachmentViewer";

type KindFilter = "All" | LedgerRow["kind"];

interface Props {
  report: ReportData;
  onOpenFiles: (files: string[]) => void;
  onExportExcel: () => void;
  onExportPdf: () => void;
  exportingPdf: boolean;
}

/** Search / type filter / sort only change which rows are shown — never a balance. */
export const filterLedger = (rows: LedgerRow[], search: string, kind: KindFilter, newestFirst: boolean) => {
  const q = search.trim().toLowerCase();
  const filtered = rows.filter(
    (r) =>
      (kind === "All" || r.kind === kind) &&
      (!q || r.label.toLowerCase().includes(q) || r.description.toLowerCase().includes(q))
  );
  // Backend returns rows oldest-first in running-balance order.
  return newestFirst ? [...filtered].reverse() : filtered;
};

/**
 * Transaction ledger — separate from the financial report, with its own
 * exports. Print Report / Download Report PDF never include it.
 *
 * The combined Expense / Income ledger with a running balance, seeded with
 * the carry forward. Every amount — including each row's running balance —
 * comes from the backend.
 */
const LedgerTable: React.FC<Props> = ({ report, onOpenFiles, onExportExcel, onExportPdf, exportingPdf }) => {
  const [search, setSearch] = useState("");
  const [kind, setKind] = useState<KindFilter>("All");
  const [newestFirst, setNewestFirst] = useState(false);

  const rows = useMemo(() => filterLedger(report.ledger, search, kind, newestFirst), [report.ledger, search, kind, newestFirst]);

  const forwardRow = report.period.startDate ? (
    <TableRow sx={{ backgroundColor: COLOR.neutralBg }}>
      <TableCell sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>{report.period.startDate}</TableCell>
      <TableCell colSpan={2} sx={{ fontStyle: "italic", color: "text.secondary" }}>
        Carry Forward
      </TableCell>
      <TableCell />
      <TableCell />
      <TableCell align="right" sx={{ fontStyle: "italic", fontWeight: 700, color: balanceColor(report.carryForward) }}>
        {fmt(report.carryForward)}
      </TableCell>
    </TableRow>
  ) : null;

  return (
    <Card elevation={2} sx={{ borderRadius: 2, mb: 3 }}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1.5,
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          Transactions
        </Typography>
        <Chip size="small" label={`${rows.length} entries`} sx={{ backgroundColor: COLOR.headerBg, fontWeight: 600 }} />
        <Box display="flex" gap={1} alignItems="center" sx={{ ml: "auto" }} flexWrap="wrap">
          <TextField select size="small" label="Type" value={kind} onChange={(e) => setKind(e.target.value as KindFilter)} sx={{ width: 120 }}>
            {["All", "Income", "Expense", "Deposit"].map((k) => (
              <MenuItem key={k} value={k}>
                {k}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch />
                </InputAdornment>
              ),
            }}
            sx={{ width: 180 }}
          />
          <Button size="small" variant="outlined" startIcon={<FileDownloadIcon />} onClick={onExportExcel}>
            Export Transactions Excel
          </Button>
          <Button size="small" variant="outlined" color="error" startIcon={<PictureAsPdfRoundedIcon />} onClick={onExportPdf} disabled={exportingPdf}>
            {exportingPdf ? "Preparing…" : "Transactions PDF"}
          </Button>
          <Tooltip title={newestFirst ? "Showing newest first" : "Showing oldest first"}>
            <IconButton size="small" onClick={() => setNewestFirst((v) => !v)}>
              <SwapVertIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer sx={{ maxHeight: 520, overflowX: "auto" }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, backgroundColor: COLOR.headerBg }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 700, backgroundColor: COLOR.headerBg }}>Category / Source</TableCell>
              <TableCell sx={{ fontWeight: 700, backgroundColor: COLOR.headerBg, minWidth: 180 }}>Description</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: COLOR.redBg, color: COLOR.redDark }}>
                Expense
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: COLOR.greenBg, color: COLOR.greenDark }}>
                Income
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, backgroundColor: COLOR.headerBg }}>
                Balance
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!newestFirst && forwardRow}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: "text.secondary", py: 4 }}>
                  No transactions found for this period.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>{row.date}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.label}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontSize: "0.7rem",
                        ...(row.kind === "Deposit" ? { borderColor: COLOR.purple, color: COLOR.purpleDark } : {}),
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>
                    <Box display="flex" alignItems="center" gap={0.75}>
                      <span>{row.description || "—"}</span>
                      <AttachmentButton count={row.attachments.length} onClick={() => onOpenFiles(row.attachments)} />
                    </Box>
                  </TableCell>
                  <TableCell align="right" sx={{ color: COLOR.redDark, fontWeight: 600, whiteSpace: "nowrap" }}>
                    {row.expenseAmount != null ? `-${fmt(row.expenseAmount)}` : ""}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ color: row.kind === "Deposit" ? COLOR.purpleDark : COLOR.greenDark, fontWeight: 600, whiteSpace: "nowrap" }}
                  >
                    {row.incomeAmount != null ? `+${fmt(row.incomeAmount)}` : ""}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: "nowrap", color: balanceColor(row.runningBalance) }}>
                    {fmt(row.runningBalance)}
                  </TableCell>
                </TableRow>
              ))
            )}
            {newestFirst && forwardRow}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell colSpan={3} sx={{ fontWeight: 700, color: "text.primary", backgroundColor: COLOR.neutralBg }}>
                Total
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: COLOR.redDark, backgroundColor: COLOR.neutralBg, whiteSpace: "nowrap" }}>
                -{fmt(report.expenseTotal)}
              </TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, color: COLOR.greenDark, backgroundColor: COLOR.neutralBg, whiteSpace: "nowrap" }}>
                +{fmt(report.incomeTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{ fontWeight: 700, color: balanceColor(report.balanceOnHand), backgroundColor: COLOR.neutralBg, whiteSpace: "nowrap" }}
              >
                {fmt(report.balanceOnHand)}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    </Card>
  );
};

export default LedgerTable;
